/**
 * MarketplaceItemCard
 * Renders auction and depot items in the same visual style as inventory ItemCard.
 * - small/medium: horizontal list layout (thumbnail + details side by side)
 * - large/x-large: vertical grid card (thumbnail on top, name + info below)
 *
 * Supports marketplace-specific metadata: auction status, time remaining,
 * bid count, depot name, seller info, etc.
 */

import React from 'react';
import { ThumbnailSize } from '../../types/viewComposition';
import { sizeToLayout } from '../../types/viewComposition';
import API_BASE_URL from '../../config';
import '../../styles/components/itemCard.css';

const CONDITION_LABELS: Record<string, string> = {
  new: 'New', like_new: 'Like New', good: 'Good', fair: 'Fair', poor: 'Poor',
};

const centsToDisplay = (cents: number) => (cents / 100).toFixed(2);

const formatTimeRemaining = (endsAt: string): string => {
  const now = new Date();
  const end = new Date(endsAt);
  const diff = end.getTime() - now.getTime();
  if (diff <= 0) return 'Ended';
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
};

const formatDistance = (miles: number | null): string | null => {
  if (miles === null) return null;
  if (miles < 1) return '<1 mi away';
  return `~${Math.round(miles)} mi away`;
};

export interface MarketplaceItem {
  id: number;
  _type: 'auction' | 'depot';
  _isMine?: boolean;
  // Common
  image_id?: number | null;
  name?: string;
  item_name?: string;
  item_id?: number;
  condition?: string;
  item_condition?: string;
  description?: string;
  item_description?: string;
  quantity?: number;
  sale_price?: number | null;
  // Depot-specific
  depot_id?: number;
  depot_name?: string;
  owner_name?: string;
  owner_id?: number;
  distance?: { label?: string } | null;
  owner_location?: string;
  // Auction-specific
  seller_name?: string;
  seller_id?: number;
  seller_location?: string;
  distance_miles?: number | null;
  status?: string;
  outcome?: string;
  ends_at?: string;
  minimum_bid?: number;
  reserve_price?: number | null;
  reserve_visible?: boolean;
  winning_bid?: number | null;
  bid_count?: number;
  auction_group?: string;
  [key: string]: any;
}

interface MarketplaceItemCardProps {
  item: MarketplaceItem;
  size: ThumbnailSize;
  currentUserId?: number | null;
  onClick?: (item: MarketplaceItem) => void;
  onImageClick?: (item: MarketplaceItem) => void;
  onNameClick?: (item: MarketplaceItem) => void;
  onDepotClick?: (item: MarketplaceItem) => void;
  onShareClick?: (item: MarketplaceItem, e: React.MouseEvent) => void;
  onBidClick?: (item: MarketplaceItem, e: React.MouseEvent) => void;
  hideDepotName?: boolean;
  isHighlighted?: boolean;
  isSelected?: boolean;
  highlightRef?: React.Ref<HTMLDivElement>;
  expandedDescs?: Set<string>;
  onToggleDesc?: (key: string) => void;
}

export const MarketplaceItemCard: React.FC<MarketplaceItemCardProps> = ({
  item,
  size,
  currentUserId,
  onClick,
  onImageClick,
  onNameClick,
  onDepotClick,
  onShareClick,
  onBidClick,
  hideDepotName,
  isHighlighted,
  isSelected,
  highlightRef,
  expandedDescs,
  onToggleDesc,
}) => {
  const layout = sizeToLayout(size);
  const isListLayout = layout === 'list';
  const isDepot = item._type === 'depot';
  const isMine = item._isMine || (!isDepot && item.seller_id === currentUserId);

  // Normalize fields
  const itemName = isDepot ? (item.name || '') : (item.item_name || item.name || '');
  const itemId = isDepot ? item.id : (item.item_id || item.id);
  const condition = isDepot ? item.condition : (item.item_condition || item.condition);
  const description = isDepot ? item.description : (item.item_description || item.description);
  const ownerName = isDepot ? item.owner_name : item.seller_name;
  const location = isDepot
    ? (item.owner_location || null)
    : (item.seller_location || null);

  const cardKey = isDepot ? `depot-${item.depot_id}-${item.id}` : `${item.id}`;
  const isExpanded = expandedDescs?.has(cardKey);

  // ID + Condition line
  const idParts: string[] = [];
  if (itemId) idParts.push(`#${itemId}`);
  if (condition && CONDITION_LABELS[condition]) idParts.push(CONDITION_LABELS[condition]);

  // Auction status
  const auctionStatus = !isDepot ? (item.status || 'active') : null;
  const isActiveAuction = auctionStatus === 'active';

  const statusBadge = () => {
    if (isDepot || !auctionStatus) return null;
    const label = isActiveAuction ? 'Active'
      : auctionStatus === 'closed' ? (item.outcome === 'sold' ? 'Sold' : 'Unsold')
      : auctionStatus === 'completed' ? 'Completed'
      : auctionStatus.charAt(0).toUpperCase() + auctionStatus.slice(1);
    return label;
  };

  // Build auction detail line
  const auctionInfo = () => {
    if (isDepot) return null;
    const details: string[] = [];
    if (isActiveAuction) {
      if (item.ends_at) details.push(formatTimeRemaining(item.ends_at));
      if (item.minimum_bid != null) details.push(`min $${centsToDisplay(item.minimum_bid)}`);
      if (item.reserve_price != null && item.reserve_visible) {
        const reserveMet = item.winning_bid != null && item.winning_bid >= item.reserve_price;
        details.push(reserveMet ? 'Reserve met' : 'Reserve not met');
      }
      if (item.bid_count && item.bid_count > 0) details.push(`${item.bid_count} bid${item.bid_count !== 1 ? 's' : ''}`);
    } else {
      const badge = statusBadge();
      if (badge) details.push(badge.toLowerCase());
      if (item.winning_bid) details.push(`$${centsToDisplay(item.winning_bid)}`);
    }
    return details;
  };

  // Depot price line
  const depotPriceInfo = () => {
    if (!isDepot) return null;
    const priceText = item.sale_price != null && item.sale_price > 0
      ? `$${centsToDisplay(item.sale_price)}`
      : 'Free';
    const qty = item.quantity != null && item.quantity > 1 ? ` · Qty: ${item.quantity}` : '';
    return <><span style={{ color: '#16a34a', fontWeight: 600 }}>{priceText}</span>{qty}</>;
  };

  // Thumbnail rendering
  const thumbSizePx = size === 'small' ? 96 : size === 'medium' ? 110 : size === 'large' ? 160 : undefined;
  const renderThumbnail = () => {
    if (isListLayout) {
      // Fixed size for list
      const style: React.CSSProperties = {
        width: thumbSizePx, height: thumbSizePx, borderRadius: 8,
        objectFit: 'cover', background: 'var(--color-bg-tertiary)', flexShrink: 0,
        cursor: onImageClick ? 'pointer' : 'default',
      };
      if (item.image_id) {
        return (
          <img
            src={`${API_BASE_URL}/api/images/${item.image_id}/public`}
            alt={itemName}
            style={style}
            onClick={(e) => { e.stopPropagation(); onImageClick?.(item); }}
          />
        );
      }
      return (
        <div style={{
          ...style, display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: size === 'medium' ? '2rem' : '1.75rem', fontWeight: 600,
          color: 'var(--color-text-faint)',
          background: 'linear-gradient(135deg, var(--color-bg-tertiary) 0%, var(--color-border) 100%)',
        }}>
          {(itemName || '?').substring(0, 2).toUpperCase()}
        </div>
      );
    }
    // Grid layout: full-width aspect-ratio thumbnail
    if (item.image_id) {
      return (
        <div style={{ width: '100%', aspectRatio: '1/1', overflow: 'hidden', cursor: onImageClick ? 'pointer' : 'default' }}
          onClick={(e) => { e.stopPropagation(); onImageClick?.(item); }}
        >
          <img
            src={`${API_BASE_URL}/api/images/${item.image_id}/public`}
            alt={itemName}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        </div>
      );
    }
    return (
      <div style={{
        width: '100%', aspectRatio: '1/1', display: 'flex', alignItems: 'center',
        justifyContent: 'center', fontSize: '3rem', fontWeight: 600,
        color: 'var(--color-text-faint)',
        background: 'linear-gradient(135deg, var(--color-bg-tertiary) 0%, var(--color-border) 100%)',
      }}>
        {(itemName || '?').substring(0, 2).toUpperCase()}
      </div>
    );
  };

  // Description (collapsible)
  const renderDescription = () => {
    if (!description) return null;
    const maxLen = isListLayout ? 60 : 40;
    return (
      <div
        className="item-description"
        style={{
          cursor: 'pointer',
          ...(!isListLayout ? { textAlign: 'center', padding: '0 8px 8px' } : {}),
        }}
        onClick={(e) => { e.stopPropagation(); onToggleDesc?.(cardKey); }}
      >
        {isExpanded ? description : (
          description.length > maxLen ? (
            <>{description.substring(0, maxLen)}… <span style={{ color: 'var(--color-accent)', fontWeight: 500 }}>more</span></>
          ) : description
        )}
      </div>
    );
  };

  // ---- LIST LAYOUT ----
  if (isListLayout) {
    const listSizeClass = size === 'large' ? 'item-card-list-large' : size === 'medium' ? 'item-card-list-medium' : '';
    return (
      <div
        ref={highlightRef}
        className={`item-card-list ${listSizeClass} ${isSelected ? 'selected' : ''}`}
        style={{
          cursor: onClick ? 'pointer' : 'default',
          ...(isHighlighted ? { background: 'var(--color-accent-light, #e0f0ff)', borderLeft: '3px solid var(--color-accent)' } : {}),
        }}
        onClick={() => onClick?.(item)}
      >
        <div style={{ position: 'relative', flexShrink: 0 }}>
          {renderThumbnail()}
          {/* Floating Bid button on list thumbnail */}
          {!isDepot && onBidClick && (
            <button
              onClick={(e) => { e.stopPropagation(); onBidClick(item, e); }}
              style={{
                position: 'absolute', bottom: 4, right: 4,
                padding: '3px 10px', borderRadius: 12, border: 'none',
                background: '#c2410c', color: '#fff', cursor: 'pointer',
                fontSize: 11, fontWeight: 700, letterSpacing: 0.3,
                boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
              }}
            >
              Bid
            </button>
          )}
        </div>
        <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
          {/* Name + share button */}
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
            <div
              className="item-name"
              style={{ flex: 1, cursor: onNameClick ? 'pointer' : 'default' }}
              onClick={onNameClick ? (e) => { e.stopPropagation(); onNameClick(item); } : undefined}
            >
              {itemName}
            </div>
            {!isDepot && onShareClick && (
              <button
                onClick={(e) => { e.stopPropagation(); onShareClick(item, e); }}
                style={{
                  flexShrink: 0, background: 'none', border: 'none', padding: '2px 4px',
                  cursor: 'pointer', color: 'var(--color-text-faint)', transition: 'color 0.15s',
                }}
                title="Share auction link"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"></path>
                  <polyline points="16 6 12 2 8 6"></polyline>
                  <line x1="12" y1="2" x2="12" y2="15"></line>
                </svg>
              </button>
            )}
          </div>
          {/* ID + Condition */}
          {idParts.length > 0 && (
            <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', fontStyle: 'italic' }}>
              {idParts.join(' · ')}
            </div>
          )}
          {/* Owner · location */}
          {(ownerName || location) && (
            <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', fontStyle: 'italic' }}>
              {ownerName}{ownerName && location ? ' · ' : ''}{location}
            </div>
          )}
          {/* Type-specific info line */}
          <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
            {isDepot ? (
              <>
                {!hideDepotName && (<><span
                  style={{ color: 'var(--color-accent)', cursor: 'pointer', fontWeight: 600 }}
                  onClick={(e) => { e.stopPropagation(); onDepotClick?.(item); }}
                >{item.depot_name}</span>{': '}</>)}
                {depotPriceInfo()}
              </>
            ) : (
              <>
                <span style={{ color: '#c2410c', fontWeight: 600 }}>Auction</span>
                {(() => { const info = auctionInfo(); return info && info.length > 0 ? ': ' + info.join(' · ') : ''; })()}
              </>
            )}
          </div>
          {/* Description */}
          {renderDescription()}
        </div>
      </div>
    );
  }

  // ---- GRID LAYOUT ----
  return (
    <div
      ref={highlightRef}
      className={`item-card-grid item-card-${size}${isSelected ? ' selected' : ''}`}
      style={{
        cursor: onClick ? 'pointer' : 'default',
        position: 'relative',
        ...(isHighlighted ? { boxShadow: '0 0 0 3px var(--color-accent)' } : {}),
      }}
      onClick={() => onClick?.(item)}
    >
      {/* Thumbnail */}
      <div style={{ position: 'relative' }}>
        {renderThumbnail()}
        {/* Auction status badge overlay */}
        {!isDepot && auctionStatus && (
          <div style={{
            position: 'absolute', top: 8, left: 8,
            padding: '2px 8px', borderRadius: 6, fontSize: 'var(--font-size-xs)', fontWeight: 700,
            background: isActiveAuction ? '#22c55e' : '#6b7280', color: '#fff',
          }}>
            {isActiveAuction && item.ends_at ? formatTimeRemaining(item.ends_at) : statusBadge()}
          </div>
        )}
        {/* Share button overlay */}
        {!isDepot && onShareClick && (
          <button
            onClick={(e) => { e.stopPropagation(); onShareClick(item, e); }}
            style={{
              position: 'absolute', top: 8, right: 8,
              width: 28, height: 28, borderRadius: '50%', border: 'none',
              background: 'rgba(0,0,0,0.5)', color: '#fff', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
            title="Share"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"></path>
              <polyline points="16 6 12 2 8 6"></polyline>
              <line x1="12" y1="2" x2="12" y2="15"></line>
            </svg>
          </button>
        )}
        {/* Floating Bid button on grid thumbnail */}
        {!isDepot && onBidClick && (
          <button
            onClick={(e) => { e.stopPropagation(); onBidClick(item, e); }}
            style={{
              position: 'absolute', bottom: 8, right: 8,
              padding: '4px 14px', borderRadius: 14, border: 'none',
              background: '#c2410c', color: '#fff', cursor: 'pointer',
              fontSize: 12, fontWeight: 700, letterSpacing: 0.3,
              boxShadow: '0 2px 6px rgba(0,0,0,0.35)',
            }}
          >
            Bid
          </button>
        )}
      </div>
      {/* Name */}
      <div
        className="item-card-name"
        onClick={onNameClick ? (e) => { e.stopPropagation(); onNameClick(item); } : undefined}
        style={{ cursor: onNameClick ? 'pointer' : 'default' }}
      >
        {itemName}
      </div>
      {/* ID + Condition */}
      {idParts.length > 0 && (
        <div style={{ padding: '0 8px', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', textAlign: 'center', fontStyle: 'italic' }}>
          {idParts.join(' · ')}
        </div>
      )}
      {/* Owner · location */}
      {(ownerName || location) && (
        <div style={{ padding: '0 8px', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', textAlign: 'center', fontStyle: 'italic' }}>
          {ownerName}{ownerName && location ? ' · ' : ''}{location}
        </div>
      )}
      {/* Type-specific info */}
      <div style={{ padding: '0 8px 4px', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', textAlign: 'center' }}>
        {isDepot ? (
          <>
            {!hideDepotName && (<><span
              style={{ color: 'var(--color-accent)', cursor: 'pointer', fontWeight: 600 }}
              onClick={(e) => { e.stopPropagation(); onDepotClick?.(item); }}
            >{item.depot_name}</span>{': '}</>)}
            {depotPriceInfo()}
          </>
        ) : (
          <>
            <span style={{ color: '#c2410c', fontWeight: 600 }}>Auction</span>
            {(() => {
              const info = auctionInfo();
              if (!info || info.length === 0) return null;
              // In grid, show minimal info
              const brief = info.slice(0, 2);
              return ': ' + brief.join(' · ');
            })()}
          </>
        )}
      </div>
      {/* Description */}
      {renderDescription()}
    </div>
  );
};
